// client/src/CalendarView.jsx
// Main calendar view — fetches events from API and renders the team calendar

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { api } from "./api.js";

const FONT = "'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', monospace";
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const START_HOUR = 6;
const END_HOUR = 23;
const HOUR_PX = 60;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
const DAY_START_MIN = START_HOUR * 60;
const DAY_END_MIN = END_HOUR * 60;
const GRID_HEIGHT = (END_HOUR - START_HOUR) * HOUR_PX;

// Light theme tokens
const T = {
  pageBg: "#f4f6fb",
  surface: "#ffffff",
  border: "#e2e8f0",
  borderFaint: "#eef2f7",
  text: "#1e293b",
  textMuted: "#64748b",
  textFaint: "#94a3b8",
  accent: "#2563eb",
  accentBg: "#eff6ff",
  accentBorder: "#bfdbfe",
  danger: "#dc2626",
  dangerBg: "#fef2f2",
  dangerBorder: "#fecaca",
  success: "#16a34a",
};

const COLORS = [
  { bg: "#e8f0fe", border: "#4285F4", text: "#174ea6", tag: "#d2e3fc" },
  { bg: "#fce8e6", border: "#EA4335", text: "#a50e0e", tag: "#fad2cf" },
  { bg: "#e6f4ea", border: "#34A853", text: "#137333", tag: "#ceead6" },
  { bg: "#fef7e0", border: "#F9AB00", text: "#8a5a00", tag: "#feefc3" },
  { bg: "#f3e8fd", border: "#A142F4", text: "#681da8", tag: "#e9d2fd" },
  { bg: "#e0f7fa", border: "#00ACC1", text: "#00697a", tag: "#c7eef4" },
  { bg: "#fde7f3", border: "#E91E63", text: "#a3055f", tag: "#fbd3e9" },
  { bg: "#feefe3", border: "#FF9800", text: "#995200", tag: "#fedfc8" },
];

function getWeekStart(d) { const r = new Date(d); r.setDate(r.getDate() - r.getDay()); r.setHours(0, 0, 0, 0); return r; }
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function formatHour(h) { if (h === 0) return "12a"; if (h < 12) return `${h}a`; if (h === 12) return "12p"; return `${h - 12}p`; }
function formatWeekRange(ws) {
  const we = new Date(ws); we.setDate(we.getDate() + 6);
  if (ws.getMonth() === we.getMonth()) return `${ws.toLocaleDateString("en-US", { month: "long" })} ${ws.getDate()} – ${we.getDate()}, ${we.getFullYear()}`;
  return `${ws.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${we.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

// Assign side-by-side columns to overlapping events.
// Events are grouped into clusters of transitively-overlapping events; within a
// cluster each event gets the first column whose previous occupant has ended,
// and every event in the cluster shares the cluster's total column count.
function layoutDayEvents(dayEvents) {
  const sorted = [...dayEvents].sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
  const positioned = [];
  let cluster = [];
  let clusterEnd = -1;

  const flushCluster = () => {
    if (cluster.length === 0) return;
    const colEnds = [];
    for (const ev of cluster) {
      let col = colEnds.findIndex(end => end <= ev.startMin);
      if (col === -1) { col = colEnds.length; colEnds.push(0); }
      colEnds[col] = ev.endMin;
      ev.col = col;
    }
    for (const ev of cluster) { ev.cols = colEnds.length; positioned.push(ev); }
    cluster = [];
  };

  for (const ev of sorted) {
    if (cluster.length > 0 && ev.startMin >= clusterEnd) { flushCluster(); clusterEnd = -1; }
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.endMin);
  }
  flushCluster();
  return positioned;
}

export function CalendarView({ user, org, orgs, onSwitchOrg, onLogout }) {
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [visible, setVisible] = useState(new Set());
  const [viewMode, setViewMode] = useState("week");
  const [tooltip, setTooltip] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orgDetails, setOrgDetails] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [now, setNow] = useState(new Date());
  const knownMemberIds = useRef(null);
  const refreshedOrgs = useRef(new Set());

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);
  const today = now;
  const gridCols = "52px repeat(7, 1fr)";

  // Keep the "now" line current
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date(weekStart); start.setDate(start.getDate() - 1);
      const end = new Date(weekStart); end.setDate(end.getDate() + 8);
      const data = await api.getEvents(org.id, start, end);
      setMembers(data.members);
      setEvents(data.events.map(e => ({ ...e, startTime: new Date(e.startTime), endTime: new Date(e.endTime) })));
      // Preserve the user's visibility toggles across refetches; newly added
      // members default to visible, removed members are dropped.
      const currentIds = new Set(data.members.map(m => m.id));
      setVisible(prev => {
        const next = new Set();
        for (const id of currentIds) {
          if (knownMemberIds.current === null || !knownMemberIds.current.has(id) || prev.has(id)) next.add(id);
        }
        return next;
      });
      knownMemberIds.current = currentIds;
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [org.id, weekStart]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Refresh all ICS feeds once per org on load (background), then re-fetch
  useEffect(() => {
    if (refreshedOrgs.current.has(org.id)) return;
    refreshedOrgs.current.add(org.id);
    api.getOrg(org.id)
      .then(data => (data?.members?.length > 0
        ? Promise.allSettled(data.members.map(m => api.refreshMember(m.id)))
        : null))
      .then(res => { if (res) fetchEvents(); })
      .catch(() => {});
  }, [org.id, fetchEvents]);

  // Fetch org details (for settings panel)
  useEffect(() => {
    api.getOrg(org.id).then(setOrgDetails).catch(() => {});
  }, [org.id]);

  // Refresh button: re-fetch every feed, then reload events
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.allSettled(members.map(m => api.refreshMember(m.id)));
    } catch (err) {
      console.error("Refresh error:", err);
    }
    await fetchEvents();
  }, [members, fetchEvents]);

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Remove this calendar?")) return;
    try {
      await api.deleteMember(memberId);
      fetchEvents();
      api.getOrg(org.id).then(setOrgDetails).catch(() => {});
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to remove calendar: " + err.message);
    }
  };

  const toggleMember = (id) => {
    setVisible(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const getEventsForDay = (day) => {
    return events.filter(e => visible.has(e.memberId) && isSameDay(e.startTime, day));
  };

  // Timed events for a day, clamped to the visible window, with overlap layout
  const getPositionedEvents = (day) => {
    const timed = [];
    for (const e of getEventsForDay(day)) {
      if (e.allDay) continue;
      const rawStart = e.startTime.getHours() * 60 + e.startTime.getMinutes();
      const rawEnd = isSameDay(e.endTime, day)
        ? e.endTime.getHours() * 60 + e.endTime.getMinutes()
        : 24 * 60; // runs past midnight — clamp to end of day
      if (rawEnd <= DAY_START_MIN || rawStart >= DAY_END_MIN) continue; // fully outside visible hours
      const startMin = Math.max(rawStart, DAY_START_MIN);
      const endMin = Math.min(Math.max(rawEnd, startMin + 20), DAY_END_MIN); // min 20-min display height
      timed.push({ event: e, startMin, endMin });
    }
    return layoutDayEvents(timed);
  };

  const getAvail = (day, hour) => {
    const hs = new Date(day); hs.setHours(hour, 0, 0, 0);
    const he = new Date(day); he.setHours(hour + 1, 0, 0, 0);
    let busy = 0, total = 0;
    members.forEach(m => {
      if (!visible.has(m.id)) return; total++;
      if (events.some(e => e.memberId === m.id && !e.allDay && e.startTime < he && e.endTime > hs)) busy++;
    });
    return { busy, total, free: total - busy };
  };

  const allDayEvents = useMemo(() => weekDays.map(d => getEventsForDay(d).filter(e => e.allDay)), [events, visible, weekDays]);
  const hasAllDay = allDayEvents.some(d => d.length > 0);

  const handleMemberAdded = () => {
    setShowAdd(false);
    fetchEvents();
    api.getOrg(org.id).then(setOrgDetails).catch(() => {});
  };

  const showTooltip = (e, ev) => setTooltip({
    summary: e.summary,
    memberName: e.memberName,
    time: `${e.startTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} – ${e.endTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`,
    location: e.location,
    x: ev.clientX, y: ev.clientY,
  });

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const btn = { padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT };

  return (
    <div style={{ fontFamily: FONT, minHeight: "100vh", background: T.pageBg, color: T.text }}>
      {/* Header */}
      <header style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#fff" }}>OC</div>
          <div>
            {orgs.length > 1 ? (
              <select
                value={org.id}
                onChange={e => { const next = orgs.find(o => o.id === e.target.value); if (next) onSwitchOrg(next); }}
                style={{ fontSize: 15, fontWeight: 700, color: T.text, border: "none", background: "transparent", fontFamily: FONT, cursor: "pointer", padding: 0 }}>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{org.name}</div>
            )}
            <div style={{ fontSize: 10, color: T.textFaint, fontFamily: MONO }}>{members.length} calendars · {events.length} events</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; })} style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 180, textAlign: "center" }}>{formatWeekRange(weekStart)}</span>
          <button onClick={() => setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; })} style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
          <button onClick={() => setWeekStart(getWeekStart(new Date()))} style={{ padding: "5px 10px", borderRadius: 5, border: `1px solid ${T.accentBorder}`, background: T.accentBg, color: T.accent, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: MONO }}>Today</button>
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          {[["week", "Week"], ["avail", "Availability"]].map(([k, l]) => (
            <button key={k} onClick={() => setViewMode(k)} style={{ ...btn, border: `1px solid ${viewMode === k ? T.accent : T.border}`, background: viewMode === k ? T.accentBg : T.surface, color: viewMode === k ? T.accent : T.textMuted }}>{l}</button>
          ))}
          <button onClick={() => setShowSettings(!showSettings)} style={{ ...btn, border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted }}>⚙ Settings</button>
          <button onClick={handleRefresh} style={{ ...btn, border: "1px solid #bbf7d0", background: "#f0fdf4", color: T.success }}>↻ Refresh</button>
          <button onClick={() => setShowHelp(true)} style={{ ...btn, border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted }}>? Help</button>
        </div>
      </header>

      <div style={{ display: "flex", minHeight: "calc(100vh - 62px)" }}>
        {/* Sidebar */}
        <aside style={{ width: 240, borderRight: `1px solid ${T.border}`, background: T.surface, padding: "14px 0", overflowY: "auto", flexShrink: 0 }}>
          <div style={{ padding: "0 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "1px", fontFamily: MONO, marginBottom: 8 }}>Calendars</div>
            {members.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 8px", borderRadius: 6, marginBottom: 2, background: visible.has(m.id) ? T.pageBg : "transparent" }}>
                <div onClick={() => toggleMember(m.id)} style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, cursor: "pointer" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS[m.colorIndex % COLORS.length].border, opacity: visible.has(m.id) ? 1 : 0.3 }} />
                  <span style={{ fontSize: 12, fontWeight: 500, flex: 1, color: T.text, opacity: visible.has(m.id) ? 1 : 0.4 }}>{m.displayName}</span>
                  <span style={{ fontSize: 8, fontFamily: MONO, color: T.textMuted, background: T.borderFaint, padding: "1px 5px", borderRadius: 3 }}>{m.source}</span>
                </div>
                <button onClick={() => handleDeleteMember(m.id)} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 14, cursor: "pointer", padding: "0 2px", lineHeight: 1 }} title="Remove calendar">×</button>
              </div>
            ))}
            {members.length === 0 && <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.5 }}>No calendars added yet.</div>}
          </div>
          <div style={{ padding: "0 14px", marginBottom: 14 }}>
            <button onClick={() => setShowAdd(true)} style={{ width: "100%", padding: "8px 0", borderRadius: 6, border: "none", background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>+ Add Calendar</button>
          </div>
          <div style={{ padding: "0 14px" }}>
            <div style={{ fontSize: 10, color: T.textFaint, lineHeight: 1.5 }}>
              Signed in as {user.displayName}
              <br />
              <button onClick={onLogout} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 10, cursor: "pointer", fontFamily: FONT, padding: 0, marginTop: 4 }}>Sign out</button>
            </div>
          </div>
        </aside>

        {/* Calendar grid */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {loading && events.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: T.textFaint }}>Loading events...</div>
          ) : (<>
            {/* Day headers */}
            <div style={{ display: "grid", gridTemplateColumns: gridCols, position: "sticky", top: 0, zIndex: 10, background: T.surface, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ borderRight: `1px solid ${T.border}` }} />
              {weekDays.map((d, i) => {
                const t = isSameDay(d, today);
                return <div key={i} style={{ padding: "10px 4px", textAlign: "center", borderRight: `1px solid ${T.border}`, background: t ? T.accentBg : "transparent" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.textFaint, fontFamily: MONO, letterSpacing: "1px" }}>{DAY_NAMES[d.getDay()]}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: t ? T.accent : T.text }}>{d.getDate()}</div>
                </div>;
              })}
            </div>

            {hasAllDay && <div style={{ display: "grid", gridTemplateColumns: gridCols, borderBottom: `1px solid ${T.border}`, minHeight: 26, background: T.surface }}>
              <div style={{ padding: "5px 4px 0", fontSize: 9, fontFamily: MONO, color: T.textFaint, textAlign: "right" }}>ALL</div>
              {weekDays.map((_, di) => <div key={di} style={{ borderRight: `1px solid ${T.borderFaint}`, padding: "2px 0" }}>
                {allDayEvents[di].map((e, ei) => {
                  const c = COLORS[e.colorIndex % COLORS.length];
                  return <div key={ei} style={{ margin: "1px 2px", padding: "1px 5px", borderRadius: 3, fontSize: 9, fontWeight: 600, background: c.tag, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.summary || "All Day"}</div>;
                })}
              </div>)}
            </div>}

            {viewMode === "week" ? (
              <div style={{ display: "grid", gridTemplateColumns: gridCols, background: T.surface }}>
                {/* Time gutter */}
                <div style={{ position: "relative", height: GRID_HEIGHT, borderRight: `1px solid ${T.border}` }}>
                  {HOURS.map(h => (
                    <div key={h} style={{ position: "absolute", top: (h - START_HOUR) * HOUR_PX + (h === START_HOUR ? 2 : -7), right: 6, fontSize: 10, fontFamily: MONO, color: T.textFaint }}>{formatHour(h)}</div>
                  ))}
                </div>
                {/* Day columns — each is one positioned layer so overlapping events can share the width */}
                {weekDays.map((day, di) => {
                  const t = isSameDay(day, today);
                  const positioned = getPositionedEvents(day);
                  return (
                    <div key={di} style={{
                      position: "relative", height: GRID_HEIGHT,
                      borderRight: `1px solid ${T.borderFaint}`,
                      background: t ? "#f8faff" : "transparent",
                      backgroundImage: `linear-gradient(to bottom, ${T.borderFaint} 1px, transparent 1px)`,
                      backgroundSize: `100% ${HOUR_PX}px`,
                    }}>
                      {positioned.map(({ event: e, startMin, endMin, col, cols }, ei) => {
                        const c = COLORS[e.colorIndex % COLORS.length];
                        const top = (startMin - DAY_START_MIN) / 60 * HOUR_PX;
                        const height = (endMin - startMin) / 60 * HOUR_PX;
                        const widthPct = 100 / cols;
                        return <div key={ei} style={{
                          position: "absolute",
                          top, height: height - 2,
                          left: `calc(${col * widthPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          background: c.bg,
                          border: `1px solid ${T.surface}`, borderLeft: `3px solid ${c.border}`,
                          borderRadius: 4, padding: "2px 5px", fontSize: 10, fontWeight: 600,
                          color: c.text, overflow: "hidden", cursor: "pointer", lineHeight: 1.3,
                          boxSizing: "border-box", boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
                        }}
                          onMouseEnter={ev => showTooltip(e, ev)}
                          onMouseMove={ev => setTooltip(p => p ? { ...p, x: ev.clientX, y: ev.clientY } : null)}
                          onMouseLeave={() => setTooltip(null)}
                        >
                          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.summary || "Busy"}</div>
                          {height > 30 && <div style={{ fontSize: 9, opacity: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.memberName}</div>}
                        </div>;
                      })}
                      {t && nowMin >= DAY_START_MIN && nowMin < DAY_END_MIN && (
                        <div style={{ position: "absolute", left: 0, right: 0, top: (nowMin - DAY_START_MIN) / 60 * HOUR_PX, height: 2, background: "#ef4444", zIndex: 5, pointerEvents: "none" }}>
                          <div style={{ position: "absolute", left: -3, top: -3, width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ background: T.surface }}>{HOURS.map(hour => (
                <div key={hour} style={{ display: "grid", gridTemplateColumns: gridCols, minHeight: 60, borderBottom: `1px solid ${T.borderFaint}` }}>
                  <div style={{ padding: "3px 6px 0", fontSize: 10, fontFamily: MONO, color: T.textFaint, textAlign: "right", borderRight: `1px solid ${T.border}` }}>{formatHour(hour)}</div>
                  {weekDays.map((day, di) => {
                    const { busy, total, free } = getAvail(day, hour);
                    const ratio = total > 0 ? busy / total : 0;
                    const bg = total === 0 ? "transparent" : ratio === 0 ? "#f0fdf4" : ratio < 0.5 ? "#fefce8" : "#fef2f2";
                    return <div key={di} style={{ borderRight: `1px solid ${T.borderFaint}`, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {total > 0 && <span style={{ fontSize: 11, fontFamily: MONO, fontWeight: 600, color: free === total ? T.success : free === 0 ? T.danger : "#ca8a04" }}>{free}/{total}</span>}
                    </div>;
                  })}
                </div>
              ))}</div>
            )}
          </>)}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && <div style={{ position: "fixed", zIndex: 2000, left: tooltip.x + 14, top: tooltip.y - 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15)", maxWidth: 240, fontSize: 12, color: T.text, pointerEvents: "none" }}>
        <div style={{ fontWeight: 700, marginBottom: 3 }}>{tooltip.summary || "Busy"}</div>
        <div style={{ fontSize: 10, color: T.textMuted, fontFamily: MONO }}>{tooltip.time}</div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{tooltip.memberName}</div>
        {tooltip.location && <div style={{ fontSize: 10, color: T.textFaint, marginTop: 3, wordBreak: "break-all" }}>📍 {tooltip.location.substring(0, 80)}</div>}
      </div>}

      {/* Add Calendar Modal */}
      {showAdd && <AddCalendarModal orgId={org.id} onAdded={handleMemberAdded} onClose={() => setShowAdd(false)} />}

      {/* Settings Modal */}
      {showSettings && <SettingsModal org={org} orgDetails={orgDetails} onClose={() => setShowSettings(false)} user={user} />}

      {/* Help / Setup Guide Modal */}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}

// ─── Shared modal styles ──────────────────────────────────────────────────────
const modalInput = { width: "100%", padding: "9px 12px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 13, fontFamily: FONT, outline: "none", boxSizing: "border-box" };
const modalLabel = { display: "block", fontSize: 10, fontWeight: 700, color: T.textMuted, fontFamily: MONO, letterSpacing: "0.5px", marginBottom: 5, textTransform: "uppercase" };

// ─── Add Calendar Modal ───────────────────────────────────────────────────────
function AddCalendarModal({ orgId, onAdded, onClose }) {
  const [name, setName] = useState("");
  const [source, setSource] = useState("O365");
  const [method, setMethod] = useState("url");
  const [feedUrl, setFeedUrl] = useState("");
  const [icsData, setIcsData] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Enter a name."); return; }
    if (method === "url" && !feedUrl.trim()) { setError("Enter a feed URL."); return; }
    if (method === "paste" && !icsData.trim()) { setError("Paste ICS data."); return; }

    setLoading(true); setError("");
    try {
      await api.addMember({
        organizationId: orgId,
        displayName: name.trim(),
        source,
        feedUrl: method === "url" ? feedUrl.trim() : null,
        icsData: method === "paste" ? icsData : null,
      });
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div style={{ position: "relative", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: 480, maxWidth: "92vw", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 3 }}>Add Calendar</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Connect a team member's calendar</div>

        <div style={{ marginBottom: 14 }}>
          <label style={modalLabel}>Display Name</label>
          <input style={modalInput} value={name} onChange={e => { setName(e.target.value); setError(""); }} placeholder="e.g. Justin Skelton" />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={modalLabel}>Source</label>
          <select style={{ ...modalInput, cursor: "pointer" }} value={source} onChange={e => setSource(e.target.value)}>
            <option value="O365">Outlook 365</option>
            <option value="Outlook">Outlook Legacy</option>
            <option value="Gmail">Gmail / Google Calendar</option>
            <option value="iCloud">iCloud</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={modalLabel}>Method</label>
          <select style={{ ...modalInput, cursor: "pointer" }} value={method} onChange={e => { setMethod(e.target.value); setError(""); }}>
            <option value="url">ICS Feed URL (auto-refreshes)</option>
            <option value="paste">Paste ICS Data (manual)</option>
          </select>
        </div>

        {method === "url" && <div style={{ marginBottom: 14 }}>
          <label style={modalLabel}>ICS Feed URL</label>
          <div style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}`, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: T.accent, lineHeight: 1.6, marginBottom: 8 }}>
            The server will fetch this URL every 15 minutes to keep events current. See Setup Guide in the sidebar for how to get this URL.
          </div>
          <input style={{ ...modalInput, fontFamily: MONO, fontSize: 11 }} value={feedUrl} onChange={e => { setFeedUrl(e.target.value); setError(""); }} placeholder="https://outlook.office365.com/owa/calendar/..." />
        </div>}

        {method === "paste" && <div style={{ marginBottom: 14 }}>
          <label style={modalLabel}>ICS Data</label>
          <textarea style={{ ...modalInput, height: 120, resize: "vertical", fontFamily: MONO, fontSize: 10 }} value={icsData} onChange={e => { setIcsData(e.target.value); setError(""); }} placeholder={"BEGIN:VCALENDAR\n...\nEND:VCALENDAR"} />
        </div>}

        {error && <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: T.danger, marginBottom: 14 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading} style={{ padding: "9px 20px", borderRadius: 7, border: "none", background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT, opacity: loading ? 0.7 : 1 }}>{loading ? "Adding..." : "Add Calendar"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Modal ───────────────────────────────────────────────────────────
function SettingsModal({ org, orgDetails, onClose, user }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div style={{ position: "relative", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: 480, maxWidth: "92vw", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.25)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 16 }}>Team Settings</div>

        <div style={{ marginBottom: 16 }}>
          <div style={modalLabel}>Team Name</div>
          <div style={{ fontSize: 14, color: T.text }}>{org.name}</div>
        </div>

        {orgDetails?.inviteCode && <div style={{ marginBottom: 16 }}>
          <div style={modalLabel}>Invite Code</div>
          <div style={{ fontSize: 12, fontFamily: MONO, color: T.accent, background: T.accentBg, padding: "8px 12px", borderRadius: 6, border: `1px solid ${T.accentBorder}`, wordBreak: "break-all", cursor: "pointer" }} onClick={() => { navigator.clipboard?.writeText(orgDetails.inviteCode); }}>
            {orgDetails.inviteCode}
            <span style={{ fontSize: 10, color: T.textFaint, marginLeft: 8 }}>click to copy</span>
          </div>
          <div style={{ fontSize: 10, color: T.textFaint, marginTop: 4 }}>Share this code with team members so they can join.</div>
        </div>}

        {orgDetails?.users && <div style={{ marginBottom: 16 }}>
          <div style={{ ...modalLabel, marginBottom: 8 }}>Team Users</div>
          {orgDetails.users.map(u => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12, color: T.text }}>
              <span style={{ flex: 1 }}>{u.displayName}</span>
              <span style={{ fontSize: 10, color: T.textMuted }}>{u.email}</span>
              <span style={{ fontSize: 9, fontFamily: MONO, color: T.textMuted, background: T.borderFaint, padding: "1px 5px", borderRadius: 3 }}>{u.role}</span>
            </div>
          ))}
        </div>}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Help Modal (Setup Guide) ─────────────────────────────────────────────────
function HelpModal({ onClose }) {
  const [tab, setTab] = useState("o365");

  const steps = {
    o365: [
      "Go to outlook.office.com and sign in",
      "Click the Settings gear (top right) → View all Outlook settings",
      "Go to Calendar → Shared calendars",
      "Under 'Publish a calendar', select your Calendar and set permissions to 'Can view all details'",
      "Click Publish — copy the ICS link that appears",
      "Paste that URL into OrbitCal when adding your calendar",
    ],
    legacy: [
      "Log into your company's Outlook Web App (OWA) — usually mail.company.com or outlook.office.com",
      "Click the Settings gear (top right) → View all Outlook settings",
      "Go to Calendar → Shared calendars",
      "Under 'Publish a calendar', select your calendar and set permissions to 'Can view all details'",
      "Click Publish — copy the ICS link that appears",
      "Paste that URL into OrbitCal when adding your calendar",
      "Note: If publishing is unavailable, your IT department may have disabled this feature",
    ],
    gmail: [
      "Go to calendar.google.com and sign in",
      "On the left, find your calendar under 'My calendars'",
      "Click the three dots (⋮) next to it → Settings and sharing",
      "Scroll down to 'Integrate calendar'",
      "Copy the 'Secret address in iCal format' link",
      "Paste that URL into OrbitCal when adding your calendar",
    ],
    icloud: [
      "Go to icloud.com/calendar and sign in",
      "Click the share icon (📡) next to your calendar",
      "Check 'Public Calendar' and copy the link",
      "Change 'webcal://' to 'https://' in the URL",
      "Paste that URL into OrbitCal when adding your calendar",
    ],
  };

  const tabStyle = (key) => ({
    padding: "7px 16px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    border: `1px solid ${tab === key ? T.accent : T.border}`,
    background: tab === key ? T.accentBg : "transparent",
    color: tab === key ? T.accent : T.textMuted,
    cursor: "pointer",
    fontFamily: FONT,
  });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div style={{ position: "relative", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: 520, maxWidth: "92vw", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>📅 Setup Guide</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>How to get your calendar's ICS feed URL</div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <button style={tabStyle("o365")} onClick={() => setTab("o365")}>Outlook 365</button>
          <button style={tabStyle("legacy")} onClick={() => setTab("legacy")}>Outlook Legacy</button>
          <button style={tabStyle("gmail")} onClick={() => setTab("gmail")}>Gmail</button>
          <button style={tabStyle("icloud")} onClick={() => setTab("icloud")}>iCloud</button>
        </div>

        {/* Steps */}
        <div>
          {steps[tab].map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "flex-start" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{i + 1}</div>
              <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, paddingTop: 2 }}>{step}</div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div style={{ background: "#fef7e0", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: "#8a5a00", marginTop: 8, lineHeight: 1.6 }}>
          ⚠️ Keep your ICS URL private — it gives read access to your calendar events.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: T.textMuted, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>Close</button>
        </div>
      </div>
    </div>
  );
}
